import { getClassAndAsc, parseGggAtlas, parseGggCharacter, parseGggPassives } from '@/logic/ggg-parser/parser'
import Store from '@/store'
import EditingContext from '@/store/editingContext'
import Session from '@/store/session'
import { useNotifications } from '@maxroll-media-group/common-libs'
import React, { useEffect, useRef } from 'react'
import { gggAuthUrl, poeApiBaseUrl, PoeCharacterHit } from './types'

const realm = 'pc'

function useFetchPoeApi() {
  const setSpinner = EditingContext.useStateSetter('spinner')

  const { notifyError } = useNotifications()

  return React.useCallback(
    async (path: string) => {
      setSpinner((spin) => spin + 1)
      const result = fetch(`${poeApiBaseUrl}/${path}`, {
        credentials: 'include'
      })
        .then((response) => response.json())
        .then((result) => {
          if (result?.error) notifyError(result.error)
          return result
        })
        .catch((err) => {
          if (err) notifyError(err.toString())
        })
        .finally(() => {
          setSpinner((spin) => spin - 1)
        })

      return result
    },
    [notifyError, setSpinner]
  )
}

export function useLoadCharacter(type: 'planner' | 'passives' | 'atlas') {
  const fetchPoeApi = useFetchPoeApi()

  const user = Session.useSelector((state) => state.user)
  const dispatch = Store.useDispatch()
  const { notifyError } = useNotifications()

  return React.useCallback(
    async (info: PoeCharacterHit, addToExistingProfile: boolean) => {
      const [character, league] = await Promise.all([
        type !== 'atlas' ? fetchPoeApi(`poe/character?realm=${realm}&name=${info.name}`) : undefined,
        type !== 'passives' ? fetchPoeApi(`poe/league?realm=${realm}&league=${info.league}`) : undefined
      ])

      if (type !== 'atlas' && (character?.error || !character?.character)) return
      if (type === 'atlas' && (league?.error || !league?.leagueAccount)) return

      dispatch.setState((oldState) => {
        try {
          const imported = oldState.imported
          const canEditPlanner = !imported || !imported.user || imported.user.id === user?.id
          const newImported = canEditPlanner && imported ? imported : undefined

          if (type === 'planner') {
            const { charClass, ascendancy } = getClassAndAsc(character.character.class)
            const sameAscendancy = ascendancy === oldState.planner?.ascendancy && charClass === oldState.planner?.class
            const existingState = addToExistingProfile && sameAscendancy ? oldState : undefined
            const newCharacter = parseGggCharacter(character.character, existingState, league?.leagueAccount)
            return { ...newCharacter, imported: newImported }
          } else if (type === 'passives') {
            const newCharacter = parseGggPassives(character.character)
            return { ...newCharacter, imported: newImported }
          } else if (type === 'atlas') {
            const newCharacter = parseGggAtlas(league.leagueAccount)
            return { ...newCharacter, imported: newImported }
          }
        } catch (error) {
          notifyError('Error: Failed to import character.')
          console.log('Error: Failed to import character.', error)
        }

        return oldState
      })
    },
    [type, user, dispatch, fetchPoeApi, notifyError]
  )
}

function useCharacterLoaderChecker() {
  const { notifyError } = useNotifications()

  // If it's the first run we might have been redirected here from the oauth flow. Check for a connectError.
  const isFirstRun = useRef(true)
  useEffect(() => {
    if (!isFirstRun.current) return
    isFirstRun.current = false

    const queryParam = new URLSearchParams(window.location.search)
    const connectError = queryParam.get('connectError')
    if (connectError) {
      notifyError(connectError)
      queryParam.delete('connectError')
      const query = queryParam.toString()
      const url = window.location.href.split('?')[0]
      window.history?.replaceState(null, '', `${url}${query ? `?${query}` : ''}`)
    }
  }, [notifyError])
}

export function useLoadCharacters() {
  const [characters, setCharacters] = EditingContext.useState('apiCharacters')
  const [showCharImportModal, setShowCharImportModal] = React.useState(false)

  useCharacterLoaderChecker()

  const fetchPoeApi = useFetchPoeApi()

  const linkPoeCharacters = React.useCallback(() => {
    fetchPoeApi(`poe/oauth/url?redirectUri=${window.location.pathname.replace(/^\//, '')}`).then((result) => {
      if (result?.error || !result?.url?.startsWith(gggAuthUrl)) return

      window.open(result.url, '_blank')
    })
  }, [fetchPoeApi])

  const loadCharacters = React.useCallback(() => {
    fetchPoeApi(`poe/characters?realm=${realm}`).then((result) => {
      if (result?.error || !result?.characters?.length) {
        if (result && !result.hasToken) linkPoeCharacters()
        return
      }

      setCharacters(result?.characters ?? [])
      setShowCharImportModal(true)
    })
  }, [fetchPoeApi, linkPoeCharacters, setCharacters, setShowCharImportModal])

  return { characters, showCharImportModal, setShowCharImportModal, loadCharacters }
}
